import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  GoogleMap,
  Marker,
  InfoWindow,
  Autocomplete,
  useJsApiLoader
} from '@react-google-maps/api';
import { useInputDialog } from './InputModal';
import { colors } from '../theme/tokens';

const LIBRARIES = ['places'];

const STATUS_COLORS = {
  stable: '#2fb65c',
  unstable: '#f4d03f',
  competitor: '#e74c3c',
  lead: '#ff69b4',
  technical: '#9b59b6',
  inactive: '#ffffff'
};

const STATUS_LABELS = {
  stable: 'Stable Client',
  unstable: 'Unstable Client',
  competitor: 'Buying from Competitor',
  lead: 'New Lead',
  technical: 'Technical Difficulty',
  inactive: 'Inactive'
};

// Zoom level at which inactive clients auto-appear as small white dots
const INACTIVE_ZOOM_THRESHOLD = 13;

// Build a Google Maps icon from a colored SVG pin
const createColoredIcon = (color, hasUnread = false) => {
  const svg = `<svg width="32" height="42" viewBox="0 0 32 42" xmlns="http://www.w3.org/2000/svg">
    <path d="M16 0C7.2 0 0 7.2 0 16c0 11.2 16 26 16 26s16-14.8 16-26C32 7.2 24.8 0 16 0z" fill="${color}" stroke="white" stroke-width="2"/>
    ${hasUnread ? '<circle cx="24" cy="8" r="6" fill="#ff6b35" stroke="white" stroke-width="2"/>' : ''}
  </svg>`;
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: { width: 32, height: 42 },
    anchor: { x: 16, y: 42 }
  };
};

// Small white dot for inactive clients — subtle, only visible when zoomed in
const INACTIVE_ICON = (() => {
  const svg = `<svg width="10" height="10" viewBox="0 0 10 10" xmlns="http://www.w3.org/2000/svg">
    <circle cx="5" cy="5" r="4" fill="white" stroke="#888" stroke-width="1.2" opacity="0.75"/>
  </svg>`;
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: { width: 10, height: 10 },
    anchor: { x: 5, y: 5 }
  };
})();

const MAP_CENTER = { lat: -28.5, lng: 24.5 };
const MAP_ZOOM = 6;

// Dark monochrome style — uniform dark terrain so bright status markers stand out
const MAP_STYLES = [
  { elementType: 'geometry',                   stylers: [{ color: '#212121' }] },
  { elementType: 'labels.icon',                stylers: [{ visibility: 'off' }] },
  { elementType: 'labels.text.fill',           stylers: [{ color: '#757575' }] },
  { elementType: 'labels.text.stroke',         stylers: [{ color: '#212121' }] },
  { featureType: 'road',         elementType: 'geometry',        stylers: [{ color: '#383838' }] },
  { featureType: 'road',         elementType: 'geometry.stroke', stylers: [{ color: '#212121' }] },
  { featureType: 'road.highway', elementType: 'geometry',        stylers: [{ color: '#4a4a4a' }] },
  { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#1f1f1f' }] },
  { featureType: 'water',        elementType: 'geometry',        stylers: [{ color: '#17263c' }] },
  { featureType: 'water',        elementType: 'labels.text.fill', stylers: [{ color: '#515c6d' }] },
  { featureType: 'poi',          elementType: 'labels',          stylers: [{ visibility: 'off' }] },
  { featureType: 'transit',      elementType: 'labels',          stylers: [{ visibility: 'off' }] },
  { featureType: 'administrative',                elementType: 'geometry',        stylers: [{ color: '#757575' }] },
  { featureType: 'administrative.country',        elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] },
  { featureType: 'administrative.land_parcel',    elementType: 'labels',          stylers: [{ visibility: 'off' }] },
  { featureType: 'administrative.locality',       elementType: 'labels.text.fill', stylers: [{ color: '#bdbdbd' }] },
  { featureType: 'administrative.neighborhood',   elementType: 'labels',          stylers: [{ visibility: 'off' }] },
];

const ClientMap = ({
  markers,
  onMarkerClick,
  onAddMarker,
  currentUserId,
  isSalesManager = false,
  salesTeam = [],
  relocatingMode = false,
  relocatingClientId = null,
  onRelocateMarker,
  onCancelRelocate,
  jumpTarget = null
}) => {
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_KEY,
    libraries: LIBRARIES
  });

  const mapRef = useRef(null);
  const autocompleteRef = useRef(null);
  const showInput = useInputDialog();

  const [activeMarkerId, setActiveMarkerId] = useState(null);
  const [addMode, setAddMode] = useState(false);

  // Track current zoom so inactive dots auto-appear when zoomed in
  const [mapZoom, setMapZoom] = useState(MAP_ZOOM);

  // Filters — inactive is OFF by default
  const [visibleStatuses, setVisibleStatuses] = useState(() => {
    const defaults = Object.keys(STATUS_LABELS).reduce((acc, s) => ({ ...acc, [s]: true }), {});
    defaults.inactive = false;
    return defaults;
  });
  const [filterByRep, setFilterByRep] = useState(null);
  const [filterByIndustry, setFilterByIndustry] = useState(null);
  // On mobile the filter panel starts collapsed so it doesn't cover the map;
  // on desktop there's room to leave it open.
  const [legendExpanded, setLegendExpanded] = useState(() =>
    typeof window === 'undefined' ? true : window.innerWidth > 768
  );

  const toggleStatus = (status) =>
    setVisibleStatuses(prev => ({ ...prev, [status]: !prev[status] }));

  // "All checked" ignores inactive so it doesn't force-show inactive on toggle-all
  const activeStatuses = Object.keys(STATUS_LABELS).filter(s => s !== 'inactive');
  const allChecked = activeStatuses.every(s => visibleStatuses[s]);
  const toggleAll = () => {
    const next = !allChecked;
    setVisibleStatuses(prev => ({
      ...activeStatuses.reduce((acc, s) => ({ ...acc, [s]: next }), {}),
      inactive: prev.inactive  // preserve inactive's own toggle
    }));
  };

  const availableIndustries = [...new Set(markers.map(m => m.industry).filter(Boolean))].sort();

  const filteredMarkers = markers
    .filter(m => {
      if (m.status === 'inactive') {
        // Show if explicitly toggled on, OR automatically when zoomed in close enough
        return visibleStatuses.inactive || mapZoom >= INACTIVE_ZOOM_THRESHOLD;
      }
      return visibleStatuses[m.status] ?? true;
    })
    .filter(m => !filterByRep || m.claimedBy?.toString() === filterByRep)
    .filter(m => !filterByIndustry || m.industry === filterByIndustry);

  const currentUserIdStr = currentUserId?.toString();

  // Map load callback
  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  // Pan and zoom to a specific location when jumpTarget changes
  useEffect(() => {
    if (jumpTarget && mapRef.current) {
      mapRef.current.panTo({ lat: jumpTarget.lat, lng: jumpTarget.lng });
      mapRef.current.setZoom(14);
    }
  }, [jumpTarget]);

  // Map click — handles both "add client" mode and "relocate" mode
  const handleMapClick = useCallback((e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();

    if (relocatingMode) {
      onRelocateMarker(lat, lng);
      return;
    }

    if (addMode) {
      showInput('Enter the name of the new client:', {
        title: 'New Client Marker',
        icon: '📍',
        placeholder: 'e.g. Acme Auto Spares',
        confirmText: 'Add Marker',
        required: true
      }).then(clientName => {
        if (clientName) {
          onAddMarker(clientName, lat, lng);
          setAddMode(false);
        }
      });
    }
  }, [relocatingMode, addMode, onRelocateMarker, onAddMarker, showInput]);

  // Places Autocomplete — pan/zoom to the selected place
  const handlePlaceChanged = () => {
    if (!autocompleteRef.current || !mapRef.current) return;
    const place = autocompleteRef.current.getPlace();
    if (!place.geometry) return;

    if (place.geometry.viewport) {
      mapRef.current.fitBounds(place.geometry.viewport);
    } else {
      mapRef.current.panTo(place.geometry.location);
      mapRef.current.setZoom(16);
    }
  };

  if (loadError) {
    return (
      <div style={styles.errorBox}>
        Failed to load Google Maps. Check your API key.
      </div>
    );
  }

  if (!isLoaded) {
    return <div style={styles.loadingBox}>Loading map...</div>;
  }

  return (
    <div style={styles.mapWrapper}>
      {/* Relocate mode overlay */}
      {relocatingMode && (
        <div style={styles.relocateOverlay}>
          <span style={styles.relocateOverlayText}>
            📍 Click anywhere on the map to set the new location
          </span>
          <button onClick={onCancelRelocate} style={styles.relocateCancelBtn}>
            ✕ Cancel
          </button>
        </div>
      )}

      {/* Search bar — Google Places Autocomplete */}
      {!relocatingMode && (
        <div style={styles.searchWrapper}>
          <Autocomplete
            onLoad={ac => (autocompleteRef.current = ac)}
            onPlaceChanged={handlePlaceChanged}
            options={{ componentRestrictions: { country: 'za' } }}
          >
            <input
              type="text"
              placeholder="Search address or area..."
              style={styles.searchInput}
            />
          </Autocomplete>
        </div>
      )}

      {/* Add client button */}
      {!relocatingMode && (
        <div style={styles.addMarkerControl}>
          <button
            onClick={() => setAddMode(m => !m)}
            style={{ ...styles.addMarkerBtn, background: addMode ? '#e74c3c' : '#2fb65c' }}
          >
            {addMode ? '✕ Cancel' : '+ Add Client'}
          </button>
          {addMode && (
            <div style={styles.addMarkerHint}>
              Click on the map to place a new client marker
            </div>
          )}
        </div>
      )}

      <GoogleMap
        mapContainerStyle={{ ...styles.map, cursor: (relocatingMode || addMode) ? 'crosshair' : 'default' }}
        center={MAP_CENTER}
        zoom={MAP_ZOOM}
        options={{ styles: MAP_STYLES, streetViewControl: false, mapTypeControl: false, fullscreenControl: false, gestureHandling: 'greedy' }}
        onLoad={onMapLoad}
        onClick={handleMapClick}
        onZoomChanged={() => { if (mapRef.current) setMapZoom(mapRef.current.getZoom()); }}
      >
        {filteredMarkers.map((marker) => {
          const isInactive = marker.status === 'inactive';
          const hasUnread = marker.unreadCount > 0 && !isInactive;
          const color = STATUS_COLORS[marker.status] || STATUS_COLORS.lead;
          const isRelocating = relocatingMode && marker._id === relocatingClientId;

          return (
            <Marker
              key={marker._id}
              position={{ lat: marker.location.lat, lng: marker.location.lng }}
              icon={isInactive ? INACTIVE_ICON : createColoredIcon(color, hasUnread)}
              opacity={isRelocating ? 0.4 : isInactive ? 0.65 : 1}
              onClick={() => {
                if (!relocatingMode) {
                  setActiveMarkerId(marker._id);
                  onMarkerClick(marker);
                }
              }}
            />
          );
        })}

        {/* InfoWindow for the active (clicked) marker */}
        {activeMarkerId && (() => {
          const marker = filteredMarkers.find(m => m._id === activeMarkerId);
          if (!marker) return null;
          const color = STATUS_COLORS[marker.status] || STATUS_COLORS.lead;
          return (
            <InfoWindow
              position={{ lat: marker.location.lat, lng: marker.location.lng }}
              onCloseClick={() => setActiveMarkerId(null)}
              options={{ pixelOffset: { width: 0, height: -42 } }}
            >
              <div style={styles.popup}>
                <h3 style={{ margin: '0 0 8px 0', color: '#1E1E1E', fontSize: '15px' }}>
                  {marker.clientName}
                </h3>
                <div style={{ ...styles.statusBadge, background: color }}>
                  {STATUS_LABELS[marker.status]}
                </div>
                {marker.unreadCount > 0 && (
                  <div style={styles.unreadBadge}>
                    {marker.unreadCount} unread message{marker.unreadCount !== 1 ? 's' : ''}
                  </div>
                )}
                <button
                  onClick={() => setActiveMarkerId(null)}
                  style={styles.viewDetailsBtn}
                >
                  Close
                </button>
              </div>
            </InfoWindow>
          );
        })()}
      </GoogleMap>

      {/* Collapsible legend / filters */}
      <div style={styles.legend}>
        <button onClick={() => setLegendExpanded(p => !p)} style={styles.legendToggleBtn}>
          <span style={styles.legendTitle}>Filters</span>
          <span style={styles.legendChevron}>{legendExpanded ? '▲' : '▼'}</span>
        </button>

        {legendExpanded && (
          <>
            <div style={styles.legendSection}>
              <div style={styles.legendSectionHeader}>
                <span style={styles.legendSectionTitle}>Status</span>
                <button onClick={toggleAll} style={styles.toggleAllBtn}>
                  {allChecked ? 'Hide All' : 'Show All'}
                </button>
              </div>
              {Object.entries(STATUS_LABELS).filter(([s]) => s !== 'inactive').map(([status, label]) => (
                <label key={status} style={styles.legendItem}>
                  <input
                    type="checkbox"
                    checked={visibleStatuses[status]}
                    onChange={() => toggleStatus(status)}
                    style={styles.checkbox}
                  />
                  <div style={{ ...styles.legendColor, background: STATUS_COLORS[status] }} />
                  <span style={{ textDecoration: visibleStatuses[status] ? 'none' : 'line-through', color: visibleStatuses[status] ? colors.text : colors.textFaint }}>
                    {label}
                  </span>
                </label>
              ))}
              {/* Inactive — separated with a note */}
              <div style={{ borderTop: `1px solid ${colors.glassBorder}`, marginTop: 4, paddingTop: 4 }}>
                <label style={styles.legendItem}>
                  <input
                    type="checkbox"
                    checked={visibleStatuses.inactive}
                    onChange={() => toggleStatus('inactive')}
                    style={styles.checkbox}
                  />
                  <div style={{ ...styles.legendColor, background: '#ffffff', border: '1px solid #888' }} />
                  <span style={{ color: visibleStatuses.inactive ? colors.text : colors.textFaint, textDecoration: visibleStatuses.inactive ? 'none' : 'line-through' }}>
                    Inactive
                  </span>
                </label>
                <div style={{ fontSize: 10, color: colors.textFaint, paddingLeft: 24, marginTop: -2 }}>
                  {mapZoom >= INACTIVE_ZOOM_THRESHOLD
                    ? 'Auto-shown (zoomed in)'
                    : 'Auto-shows when zoomed in'}
                </div>
              </div>
              <div style={{ ...styles.legendItem, marginTop: '6px', paddingTop: '6px', borderTop: `1px solid ${colors.glassBorder}`, cursor: 'default' }}>
                <div style={{ ...styles.legendColor, background: '#ff6b35' }} />
                <span style={{ color: colors.textMuted }}>Unread Messages</span>
              </div>
            </div>

            <div style={styles.legendSection}>
              <span style={styles.legendSectionTitle}>Assigned To</span>
              {isSalesManager ? (
                <select value={filterByRep || ''} onChange={e => setFilterByRep(e.target.value || null)} style={styles.repSelect}>
                  <option value="" style={styles.selectOption}>Everyone</option>
                  {salesTeam.map(member => (
                    <option key={member._id} value={member._id} style={styles.selectOption}>
                      {member.name} ({member.salesRole === 'manager' ? 'Mgr' : 'Rep'})
                    </option>
                  ))}
                </select>
              ) : (
                <label style={{ ...styles.legendItem, marginBottom: 0 }}>
                  <input
                    type="checkbox"
                    checked={filterByRep === currentUserIdStr}
                    onChange={() => setFilterByRep(prev => prev === currentUserIdStr ? null : currentUserIdStr)}
                    style={styles.checkbox}
                  />
                  <span style={{ color: colors.text, fontSize: '12px' }}>My Clients Only</span>
                </label>
              )}
            </div>

            {availableIndustries.length > 0 && (
              <div style={styles.legendSection}>
                <span style={styles.legendSectionTitle}>Industry</span>
                <select value={filterByIndustry || ''} onChange={e => setFilterByIndustry(e.target.value || null)} style={styles.repSelect}>
                  <option value="" style={styles.selectOption}>All Industries</option>
                  {availableIndustries.map(ind => (
                    <option key={ind} value={ind} style={styles.selectOption}>{ind}</option>
                  ))}
                </select>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const styles = {
  mapWrapper: { position: 'relative', width: '100%', height: '700px' },
  map: { width: '100%', height: '700px', borderRadius: '10px' },
  loadingBox: { height: '700px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: colors.textMuted, fontSize: '16px' },
  errorBox: { height: '700px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#e74c3c', fontSize: '16px', padding: '20px', textAlign: 'center' },
  searchWrapper: {
    position: 'absolute', top: '10px', left: '10px', zIndex: 5, width: '300px', maxWidth: 'calc(100% - 20px)'
  },
  searchInput: {
    width: '100%', height: '40px', padding: '0 14px',
    fontSize: '14px', borderRadius: '8px',
    border: `1px solid ${colors.border}`,
    boxShadow: '0 2px 12px rgba(0,0,0,0.35)', outline: 'none',
    color: colors.text, background: colors.menuBg,
    backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
    boxSizing: 'border-box'
  },
  addMarkerControl: {
    position: 'absolute', top: '10px', right: '10px', zIndex: 5,
    display: 'flex', flexDirection: 'column', gap: '10px', alignItems: 'flex-end'
  },
  addMarkerBtn: {
    padding: '12px 24px', color: 'white', border: 'none',
    borderRadius: '8px', fontSize: '16px', fontWeight: '600',
    cursor: 'pointer', boxShadow: '0 2px 10px rgba(0,0,0,0.2)'
  },
  addMarkerHint: {
    background: colors.menuBg, padding: '10px 15px', borderRadius: '8px',
    fontSize: '14px', boxShadow: '0 2px 10px rgba(0,0,0,0.35)',
    border: `1px solid ${colors.border}`,
    backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
    color: colors.text, maxWidth: '200px', textAlign: 'center'
  },
  relocateOverlay: {
    position: 'absolute', top: '10px', right: '10px', zIndex: 6,
    display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px'
  },
  relocateOverlayText: {
    background: colors.menuBg, color: colors.text, padding: '10px 16px',
    borderRadius: '8px', fontSize: '14px', fontWeight: '600',
    boxShadow: '0 2px 12px rgba(0,0,0,0.3)'
  },
  relocateCancelBtn: {
    padding: '10px 20px', background: '#e74c3c', color: 'white',
    border: 'none', borderRadius: '8px', fontSize: '14px',
    fontWeight: '600', cursor: 'pointer', boxShadow: '0 2px 10px rgba(0,0,0,0.2)'
  },
  popup: { minWidth: '180px', textAlign: 'center', padding: '4px' },
  statusBadge: {
    color: 'white', padding: '5px 10px', borderRadius: '15px',
    fontSize: '12px', fontWeight: '600', display: 'inline-block', marginBottom: '8px'
  },
  unreadBadge: {
    background: '#ff6b35', color: 'white', padding: '4px 8px',
    borderRadius: '10px', fontSize: '11px', fontWeight: '600', marginBottom: '8px'
  },
  viewDetailsBtn: {
    width: '100%', padding: '8px', background: '#2fb65c', color: 'white',
    border: 'none', borderRadius: '5px', cursor: 'pointer',
    fontSize: '14px', fontWeight: '600', marginTop: '6px'
  },
  legend: {
    position: 'absolute', bottom: '20px', left: '20px',
    background: colors.menuBg, borderRadius: '10px',
    border: `1px solid ${colors.border}`,
    boxShadow: '0 8px 24px rgba(0,0,0,0.45)', zIndex: 5,
    minWidth: '210px', maxWidth: 'calc(100% - 40px)', overflow: 'hidden',
    backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)'
  },
  legendToggleBtn: {
    width: '100%', display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', padding: '11px 14px',
    background: 'none', border: 'none', cursor: 'pointer', margin: 0
  },
  legendTitle: { margin: 0, fontSize: '13px', fontWeight: '700', color: colors.text },
  legendChevron: { fontSize: '10px', color: colors.textMuted },
  legendSection: { padding: '8px 14px', borderTop: `1px solid ${colors.glassBorder}` },
  legendSectionHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' },
  legendSectionTitle: {
    fontSize: '11px', fontWeight: '700', color: colors.textMuted,
    textTransform: 'uppercase', letterSpacing: '0.5px',
    display: 'block', marginBottom: '7px'
  },
  toggleAllBtn: {
    background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: '4px',
    padding: '2px 8px', fontSize: '11px', cursor: 'pointer', color: colors.textMuted, fontWeight: '600'
  },
  legendItem: {
    display: 'flex', alignItems: 'center', gap: '8px',
    marginBottom: '7px', fontSize: '12px', cursor: 'pointer', userSelect: 'none',
    color: colors.text
  },
  checkbox: { width: '14px', height: '14px', cursor: 'pointer', accentColor: '#2fb65c', flexShrink: 0 },
  legendColor: {
    width: '16px', height: '16px', borderRadius: '50%',
    border: '2px solid rgba(255,255,255,0.85)', boxShadow: '0 1px 3px rgba(0,0,0,0.4)', flexShrink: 0
  },
  repSelect: {
    width: '100%', padding: '6px 8px', borderRadius: '6px',
    border: `1px solid ${colors.border}`, fontSize: '12px', background: colors.surfaceDeep,
    cursor: 'pointer', color: colors.text
  },
  selectOption: { background: colors.surfaceSolid, color: colors.text }
};

export default ClientMap;
